import React from 'react'

/**
 * The frame height is normally, just `auto` (i.e., it expands to fit the
 * items), but in adaptiveHeight mode, it's the height of the tallest visible
 * item.
 *
 * In adaptiveHeight mode, we also switch between two states to ensure that
 * slides don't render with zero height when server-side-rendering:
 *
 * - When initializedAdaptiveHeight is false: the frame has height auto; visible
 *   slides have height auto; invisible slides have height 0
 * - The client sets initializedAdaptiveHeight to true once we've measured all
 *   the visible slides' heights
 * - When initializedAdaptiveHeight is true: the frame has height set to the
 *   tallest visible slide; all slides have height 100%
 */
export function useFrameHeight(
  adaptiveHeight: boolean,
  slidesToShow: number,
  slideCount: number
): {
  /**
   * Callback that can be passed to Slides to allow them to update the
   * `visibleHeights` variable.
   */
  handleVisibleSlideHeightChange: (slideIndex: number, height: number | null) => unknown

  /** CSS height of the frame container */
  frameHeight: string

  /**
   * Whether we'd measured the initial slide heights and are ready for the
   * frame to control the children's height, rather than the other way around.
   */
  initializedAdaptiveHeight: boolean
} {
  const [visibleHeights, setVisibleHeights, visibleHeightsRef] = useStateWithRef<
    {
      height: number
      slideIndex: number
    }[]
  >([])

  // Whether we've received heights of all initial visible heights
  const [initializedAdaptiveHeight, setInitializedAdaptiveHeight] = React.useState(false)

  const handleVisibleSlideHeightChange = React.useCallback(
    (slideIndex: number, height: number | null) => {
      // Use the ref's value since it's always the latest value
      const latestVisibleHeights = visibleHeightsRef.current
      let newVisibleHeights: {
        height: number
        slideIndex: number
      }[]
      if (height === null) {
        newVisibleHeights = latestVisibleHeights.filter((slideHeight) => slideHeight.slideIndex !== slideIndex)
      } else {
        newVisibleHeights = [...latestVisibleHeights, { slideIndex, height }]
      }
      setVisibleHeights(newVisibleHeights)

      if (newVisibleHeights.length >= Math.min(slideCount, Math.ceil(slidesToShow))) {
        setInitializedAdaptiveHeight(true)
      }
    },
    [slideCount, setVisibleHeights, slidesToShow, visibleHeightsRef]
  )

  const frameHeight = React.useMemo(() => {
    if (adaptiveHeight) {
      // We want server-side-rendering to render the carousel with non-zero
      // height. to achieve this, we first set the height to `auto` until
      // we've received the heights of the visible slides. Then, we switch to
      // a mode where the frame controls the height.
      if (!initializedAdaptiveHeight) {
        return 'auto'
      }

      const maxHeight = Math.max(0, ...visibleHeights.map((height) => height.height))
      return `${maxHeight}px`
    } else {
      return 'auto'
    }
  }, [adaptiveHeight, initializedAdaptiveHeight, visibleHeights])

  return {
    handleVisibleSlideHeightChange,
    frameHeight,
    initializedAdaptiveHeight
  }
}

export function useSlideIntersectionObserver(
  elementRef: React.RefObject<Element | null>,
  rootRef: React.RefObject<Element | null>,
  callback: (entry: IntersectionObserverEntry) => void
) {
  const [entry, setEntry] = React.useState<IntersectionObserverEntry>()
  const callbackRef = React.useRef(callback)

  React.useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  React.useEffect(() => {
    const node = elementRef?.current
    const root = rootRef?.current

    if (!window.IntersectionObserver || !node || !root) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setEntry(entry)
          callbackRef.current(entry)
        })
      },
      {
        threshold: [0.05, 0.95],
        root
      }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [elementRef, rootRef])

  return entry
}

/**
 * Like useState, but also returns a ref that's always instantly updated.
 *
 * This is useful in two cases:
 *
 * 1. You need to both force a re-render when a variable changes, and
 *    also avoid re-running side effects (for example, a network call), even
 *    before the state fully updates for the component.
 * 2. Multiple callbacks need to modify the same object or array before the
 *    state updates. For example, if one callback tries to append 4 to
 *    `[1, 2, 3]` and another tries to append 5, we may end up with only
 *    `[1, 2, 3, 5]` instead of `[1, 2, 3, 4, 5]`
 */
export const useStateWithRef = <S>(initialState: S): [S, (newValue: S) => void, React.MutableRefObject<S>] => {
  const [value, setValue] = React.useState(initialState)
  // Need to explicitly type this out, or the overloads can confuse the
  // compiler to think that this might be a React Component ref
  const valueRef = React.useRef<S>(initialState) as React.MutableRefObject<S>

  const setValueAndRef = React.useCallback((newValue: S) => {
    valueRef.current = newValue
    setValue(newValue)
  }, [])

  return [value, setValueAndRef, valueRef]
}

/**
 * Provides an interpolated value, beginning at 0 and ending at 1, based on a
 * provided duration and animation timing function.
 */
export function useTween(
  durationMs: number,
  /**
   * Animation easing function accepting a normalized time between 0 and 1,
   * inclusive, and returning an eased time, which equals 0 at normalizedTime==0
   * and equals 1 at normalizedTime==1
   */
  easingFunction: (normalizedTime: number) => number,
  // navigationNum is an combination of numbers that are stable when the
  // animation should not be running or should continue running, but change when
  // the animation should start running. In practice, this is a combination of
  // the animation distance and slide index.
  navigationNum: number,
  shouldInterrupt: boolean
) {
  const [normalizedTimeRaw, setNormalizedTime] = React.useState(1)
  const startTime = React.useRef(Date.now())
  const rAF = React.useRef<number | undefined>(undefined)
  const isFirstRender = React.useRef(true)
  const lastNavigationNum = React.useRef<number | null>(null)

  // Detect on the first render following navigation if the animation should
  // be running. If we wait for the useEffect, the first render will flash with
  // the slide in its destination position, before the animation triggers,
  // sending it back to the position of the first frame of the animation. This
  // approach is done in place of a useLayoutEffect, which has issues with SSR.
  const normalizedTime =
    lastNavigationNum.current === null || lastNavigationNum.current === navigationNum || shouldInterrupt
      ? normalizedTimeRaw
      : 0 // 0 here indicates the animation has begun

  React.useEffect(() => {
    lastNavigationNum.current = navigationNum

    // Skip the first render as we don't want to trigger the animation right off
    // the bat
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (shouldInterrupt) {
      return
    }

    startTime.current = Date.now()
    setNormalizedTime(0)

    const tick = () => {
      rAF.current = requestAnimationFrame(() => {
        const currentTime = Date.now()
        const normalizedTime = Math.min(1, (currentTime - startTime.current) / durationMs)
        setNormalizedTime(normalizedTime)

        if (normalizedTime < 1) {
          tick()
        } else {
          // Clean up so we can use this value to determine if the most recent
          // animation completed
          rAF.current = undefined
        }
      })
    }
    tick()

    return () => {
      // If the most recent animation did not complete, cut it short and reset
      // the animation
      if (rAF.current !== undefined) {
        cancelAnimationFrame(rAF.current)
        setNormalizedTime(1)
      }
    }
  }, [navigationNum, durationMs, shouldInterrupt])

  return {
    isAnimating: normalizedTime !== 1,
    value: easingFunction(normalizedTime)
  }
}

type CellAlign = 'center' | 'right' | 'left'
/**
 * Calculate the indices that each dot will jump to when clicked
 */
export function getDotIndexes(
  slideCount: number,
  slidesToScroll: number,
  slidesToShow: number,
  wrapAround: boolean,
  cellAlign: CellAlign
) {
  const dotIndexes: number[] = []
  const scrollSlides = slidesToScroll <= 0 ? 1 : slidesToScroll

  if (wrapAround) {
    for (let i = 0; i < slideCount; i += scrollSlides) {
      dotIndexes.push(i)
    }

    return dotIndexes
  }

  if (cellAlign === 'center') {
    for (let i = 0; i < slideCount - 1; i += scrollSlides) {
      dotIndexes.push(i)
    }

    if (slideCount > 0) {
      dotIndexes.push(slideCount - 1)
    }

    return dotIndexes
  }

  if (cellAlign === 'left') {
    if (slidesToShow >= slideCount) {
      return [0]
    }

    const lastPossibleIndexWithoutWhitespace = slideCount - slidesToShow

    for (let i = 0; i < lastPossibleIndexWithoutWhitespace; i += scrollSlides) {
      dotIndexes.push(i)
    }

    dotIndexes.push(dotIndexes[dotIndexes.length - 1] + scrollSlides)

    return dotIndexes
  }

  if (cellAlign === 'right') {
    if (slidesToShow >= slideCount) {
      return [slideCount - 1]
    }

    const firstPossibleIndexWithoutWhitespace = slidesToShow - 1

    for (let i = slideCount - 1; i > firstPossibleIndexWithoutWhitespace; i -= scrollSlides) {
      dotIndexes.push(i)
    }
    dotIndexes.push(dotIndexes[dotIndexes.length - 1] - scrollSlides)

    dotIndexes.reverse()

    return dotIndexes
  }

  // We should never reach this, because the if statements above cover all
  // possible values of cellAlign
  return dotIndexes
}

export function getNextMoveIndex(
  wrapAround: boolean,
  currentSlide: number,
  slideCount: number,
  slidesToScroll: number,
  slidesToShow: number,
  cellAlign: CellAlign
) {
  if (wrapAround) {
    return currentSlide + slidesToScroll
  }
  // Quit early if we're already as far right as we can go
  if (currentSlide >= slideCount - 1 || (cellAlign === 'left' && currentSlide >= slideCount - slidesToShow)) {
    return currentSlide
  }

  return Math.min(currentSlide + slidesToScroll, slideCount - 1)
}

export function getPrevMoveIndex(
  wrapAround: boolean,
  currentSlide: number,
  slidesToScroll: number,
  slidesToShow: number,
  cellAlign: CellAlign
) {
  if (wrapAround) {
    return currentSlide - slidesToScroll
  }

  // Quit early if we're already as far left as we can go
  if (currentSlide <= 0 || (cellAlign === 'right' && currentSlide <= slidesToShow - 1)) {
    return currentSlide
  }

  return Math.max(currentSlide - slidesToScroll, 0)
}

export function getDefaultSlideIndex(
  slideIndex: number | undefined,
  slideCount: number,
  slidesToShow: number,
  slidesToScroll: number,
  cellAlign: CellAlign
) {
  if (slideIndex !== undefined) {
    return slideIndex
  }

  const dotIndexes = getDotIndexes(slideCount, slidesToScroll, slidesToShow, false, cellAlign)

  return dotIndexes[0]
}

/**
 * Boils down an unbounded index (-Infinity < index < Infinity) to a bounded one
 * (0 ≤ index < slideCount)
 */
export function getBoundedIndex(rawIndex: number, slideCount: number) {
  return ((rawIndex % slideCount) + slideCount) % slideCount
}

export function getPercentOffsetForSlide(
  currentSlide: number,
  slideCount: number,
  slidesToShow: number,
  cellAlign: CellAlign,
  wrapAround: boolean
): number {
  // When wrapAround is enabled, we show the slides 3 times
  const renderedSlideCount = wrapAround ? 3 * slideCount : slideCount

  const singleSlidePercentOfWhole = 100 / renderedSlideCount

  // When wrap is on, -33.33% puts us right on the center, true set of slides
  // (the left and right sets are clones meant to avoid visual gaps)
  let slide0Offset = wrapAround ? -100 / 3 : 0

  if (cellAlign === 'right' && slidesToShow > 1) {
    const excessSlides = slidesToShow - 1
    slide0Offset += singleSlidePercentOfWhole * excessSlides
  }

  if (cellAlign === 'center' && slidesToShow > 1) {
    const excessSlides = slidesToShow - 1
    // Half of excess is on left and half is on right when centered
    const excessLeftSlides = excessSlides / 2
    slide0Offset += singleSlidePercentOfWhole * excessLeftSlides
  }

  const currentSlideOffsetFrom0 = (100 / renderedSlideCount) * currentSlide

  return slide0Offset - currentSlideOffsetFrom0
}

export function prevButtonDisabled({
  cellAlign,
  currentSlide,
  slidesToShow,
  wrapAround
}: {
  cellAlign: string
  currentSlide: number
  slidesToShow: number
  wrapAround: boolean
}) {
  // inifite carousel
  if (wrapAround) {
    return false
  }

  // disable if displaying the leftmost slide
  if (currentSlide === 0) {
    return true
  }

  // remainder scroll mode
  if (cellAlign === 'right' && currentSlide <= slidesToShow - 1) {
    return true
  }

  return false
}

export function nextButtonDisabled({
  cellAlign,
  currentSlide,
  slideCount,
  slidesToShow,
  wrapAround
}: {
  cellAlign: string
  currentSlide: number
  slidesToShow: number
  wrapAround: boolean
  slideCount: number
}) {
  // inifite carousel
  if (wrapAround) {
    return false
  }

  // If we are at the last possible slide without wrap, disable
  if (currentSlide >= slideCount - 1) {
    return true
  }

  // remainder scroll mode
  if (cellAlign === 'left' && currentSlide >= slideCount - slidesToShow) {
    return true
  }

  return false
}
